import {groupBy} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import validateProcessInteractiveFlowchart from 'processes/graph-builder-processes/flowchart-viz/util/validateProcessInteractiveFlowchart';
import constructDesignSpace from 'processes/graph-builder-processes/util/constructDesignSpace';
import {FlowchartValidationErrorType} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {
  AvailableTaskSchema,
  ClientSchemaMapperConfig,
  isPlaceholderTaskSchema,
} from './FlowchartConfigConstants';
import {FlowchartConfigTab} from './GraphBuilderFlowchartConfigTabContainer';

export interface FlowchartConfigForValidation {
  availableTaskSchemas: Array<AvailableTaskSchema>;
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  templateFlowchart: ProcessInteractiveFlowchart | null;
  isFlowchartModifiableOnCreation: boolean;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  allowNoSchemas?: boolean;
}

function doMapperConfigsCoverSameTransition(schemaMapperConfigs: Array<ClientSchemaMapperConfig>): boolean {
  assert(
    schemaMapperConfigs.every(
      ({fromTaskSchemas, toTaskSchema}) => fromTaskSchemas.length > 0 && toTaskSchema != null
    ),
    'Mapper configs must be defined'
  );
  const schemaMappersByToSchema = groupBy(schemaMapperConfigs, ({toTaskSchema}) => toTaskSchema!.id);
  for (const schemaMapperConfigsForToSchema of Object.values(schemaMappersByToSchema)) {
    for (const currMapperConfig of schemaMapperConfigsForToSchema) {
      const currFromTaskSchemaIds = new Set(currMapperConfig.fromTaskSchemas.map(({id}) => id));
      const overlapsWithOtherMapper = schemaMapperConfigsForToSchema.some((mapperConfig) => {
        if (mapperConfig.clientId === currMapperConfig.clientId) {
          return false;
        }
        const fromTaskSchemaIds = new Set(mapperConfig.fromTaskSchemas.map(({id}) => id));
        return [...currFromTaskSchemaIds].some((id) => fromTaskSchemaIds.has(id));
      });
      if (overlapsWithOtherMapper) {
        return true;
      }
    }
  }
  return false;
}

export function extractGraphBuilderErrorTypes(
  validatedFlowchart: ProcessInteractiveFlowchart
): Array<FlowchartValidationErrorType> {
  const errorTypes: Array<FlowchartValidationErrorType> = [];
  for (const node of validatedFlowchart.nodes) {
    errorTypes.push(...(node.errorTypes || []));
  }
  for (const edge of validatedFlowchart.edges) {
    errorTypes.push(...(edge.errorTypes || []));
  }
  return errorTypes;
}

function templateFlowchartErrorTypes(
  templateFlowchart: ProcessInteractiveFlowchart,
  availableTaskSchemas: Array<{id: string; name: string}>,
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  rootTaskSchema: ProcessTaskSchemaGraphQL,
  didInitialFlowchartHaveChainedRouters: boolean
): Array<FlowchartValidationErrorType> {
  const designSpace = constructDesignSpace(availableTaskSchemas, schemaMapperConfigs, rootTaskSchema);
  const validatedFlowchart = validateProcessInteractiveFlowchart(
    templateFlowchart,
    designSpace,
    didInitialFlowchartHaveChainedRouters
  );
  return extractGraphBuilderErrorTypes(validatedFlowchart);
}

export interface FlowchartConfigValidationResults {
  tab: FlowchartConfigTab | null;
  message: string;
  errorTypes: Array<FlowchartValidationErrorType>;
}

export default function getFlowchartConfigValidationResults({
  availableTaskSchemas,
  schemaMapperConfigs,
  templateFlowchart,
  isFlowchartModifiableOnCreation,
  rootTaskSchema,
  didInitialFlowchartHaveChainedRouters,
  allowNoSchemas, // allow users to save a flowchart schema with no task schemas, only used for config
}: FlowchartConfigForValidation): FlowchartConfigValidationResults | null {
  let tab;
  let message;
  let errorTypes: Array<FlowchartValidationErrorType> = [];
  if (templateFlowchart != null) {
    errorTypes = templateFlowchartErrorTypes(
      templateFlowchart,
      availableTaskSchemas as Array<{id: string; name: string}>,
      schemaMapperConfigs,
      rootTaskSchema,
      didInitialFlowchartHaveChainedRouters
    );
  }

  assert(
    availableTaskSchemas.every((taskSchema) => taskSchema != null),
    'Available task schemas must be defined'
  );

  if (availableTaskSchemas.length === 0 && allowNoSchemas !== true) {
    tab = FlowchartConfigTab.AVAILABLE_TASK_SCHEMAS;
    message = 'Flowchart config must have at least one task schema';
  } else if (availableTaskSchemas.some((taskSchema) => isPlaceholderTaskSchema(taskSchema))) {
    tab = FlowchartConfigTab.AVAILABLE_TASK_SCHEMAS;
    message = 'All task schemas must be set';
  } else if (schemaMapperConfigs.length === 0 && availableTaskSchemas.length > 0) {
    tab = FlowchartConfigTab.SCHEMA_MAPPER_CONFIGS;
    message = 'Flowchart config must have at least one transition';
  } else if (schemaMapperConfigs.some(({fromTaskSchemas}) => fromTaskSchemas.length === 0)) {
    tab = FlowchartConfigTab.SCHEMA_MAPPER_CONFIGS;
    message = 'All transitions must have a source';
  } else if (schemaMapperConfigs.some(({toTaskSchema}) => toTaskSchema == null)) {
    tab = FlowchartConfigTab.SCHEMA_MAPPER_CONFIGS;
    message = 'All transitions must have a destination';
  } else if (schemaMapperConfigs.some(({mapperConfig}) => mapperConfig == null)) {
    tab = FlowchartConfigTab.SCHEMA_MAPPER_CONFIGS;
    message = 'All transitions must have a configured mapper';
  } else if (doMapperConfigsCoverSameTransition(schemaMapperConfigs)) {
    tab = FlowchartConfigTab.SCHEMA_MAPPER_CONFIGS;
    message = 'A transition may not overlap with another transition';
  } else if (!isFlowchartModifiableOnCreation && templateFlowchart == null) {
    tab = FlowchartConfigTab.TEMPLATE_FLOWCHART;
    message = 'The flowchart must be modifiable on task creation if a template flowchart is not set';
  } else if (templateFlowchart != null && errorTypes.length > 0) {
    tab = FlowchartConfigTab.TEMPLATE_FLOWCHART;
    message = 'The template flowchart must not have any errors';
  }

  if (tab && message) {
    return {tab, message, errorTypes};
  }

  return null;
}
